import React, { useState, useEffect } from 'react';
import { X, Loader2, Check, Link, Copy, AlertCircle, Users, Globe, Database } from 'lucide-react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';

const SHARE_PREFS_KEY = 'vg_share_preferences';

/**
 * ShareChatModal - Create shareable links for chats
 *
 * Features:
 * - Public or organization-only sharing
 * - Optional attribution
 * - localStorage preferences
 * - Datasource warning
 * - One-click copy to clipboard
 */
function ShareChatModal({ isOpen, onClose, messages, datasource, mode, user, reviewContent }) {
  const [visibility, setVisibility] = useState('public');
  const [showAttribution, setShowAttribution] = useState(true); // Default to TRUE (opt-out)
  const [hideDatasource, setHideDatasource] = useState(false);
  const [note, setNote] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const userHasOrg = user?.org_id != null;

  // Load preferences from localStorage on mount (but NOT visibility - user must choose each time)
  useEffect(() => {
    if (isOpen) {
      try {
        const savedPrefs = localStorage.getItem(SHARE_PREFS_KEY);
        if (savedPrefs) {
          const prefs = JSON.parse(savedPrefs);
          if (prefs.showAttribution !== undefined) {
            setShowAttribution(prefs.showAttribution);
          } else {
            setShowAttribution(true); // Default to showing attribution
          }
          if (prefs.hideDatasource !== undefined) {
            setHideDatasource(prefs.hideDatasource);
          }
        } else {
          setShowAttribution(true); // Default to showing attribution
        }

        // Set visibility default based on org membership (not from localStorage)
        if (userHasOrg) {
          setVisibility('organization'); // Organization members default to org-only
        } else {
          setVisibility('public');
        }
      } catch (err) {
        console.error('Failed to load share preferences:', err);
      }
    }
  }, [isOpen, userHasOrg]);

  // Helper: Convert blob URL to base64
  const blobToBase64 = async (blobUrl) => {
    try {
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          // Extract base64 part (remove "data:image/png;base64," prefix)
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error('Failed to convert blob to base64:', err);
      return null;
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');

      // Transform messages to match backend schema
      const transformedMessages = await Promise.all(
        messages
          .filter((msg, index) => {
            // Include messages that have actual text content OR will get reviewContent
            const isLastMessage = index === messages.length - 1;
            const willHaveReview = isLastMessage && reviewContent;
            const hasContent = msg.content || msg.investigationThread || willHaveReview;
            return hasContent && (typeof hasContent === 'string' || willHaveReview);
          })
          .map(async (msg, index) => {
            // Use original content field (Virgo has final response, Taurus is empty)
            // DO NOT use investigationThread as content - that creates duplication
            // If this is the last message and reviewContent exists, use that instead
            const isLastMessage = index === messages.length - 1;
            const contentToUse = (isLastMessage && reviewContent) ? reviewContent : (msg.content || '');

            const transformed = {
              role: msg.type || msg.role, // 'user' or 'assistant'
              content: contentToUse,
              timestamp: msg.timestamp || new Date().toISOString(),
              // Mark if this content is from a review (for special styling in viewer)
              isReview: isLastMessage && reviewContent ? true : undefined,
              // Include response time if available (for assistant messages)
              responseTime: msg.responseTime || undefined,
              // Include deep review findings if available (Taurus only)
              deepReviewFindings: msg.deepReviewFindings || undefined,
              // Flag Taurus messages for proper rendering in shared view
              isTaurus: msg.isTaurus || undefined
            };

            // Include investigation thread if it exists
            // Taurus: investigationThread (agent output)
            // Virgo: consoleOutput (execution details)
            if (msg.investigationThread) {
              transformed.investigationThread = msg.investigationThread;
            } else if (msg.consoleOutput) {
              // Virgo uses consoleOutput instead of investigationThread
              transformed.investigationThread = msg.consoleOutput;
            }

            // Handle charts - convert blob URLs to base64
            if (msg.plots || msg.charts) {
              const rawCharts = msg.plots || msg.charts;

              if (Array.isArray(rawCharts) && rawCharts.length > 0) {
                const processedCharts = await Promise.all(
                  rawCharts.map(async (chart) => {
                    // If chart is a blob URL string, convert to object with base64
                    if (typeof chart === 'string' && chart.startsWith('blob:')) {
                      const base64Data = await blobToBase64(chart);
                      if (base64Data) {
                        return {
                          type: 'matplotlib',
                          data: base64Data
                        };
                      }
                      return null;
                    }
                    // If chart is already a data URL, extract base64
                    else if (typeof chart === 'string' && chart.startsWith('data:')) {
                      const base64Data = chart.split(',')[1];
                      return {
                        type: 'matplotlib',
                        data: base64Data
                      };
                    }
                    // If chart is a Taurus file object, fetch from Taurus server and convert to base64
                    else if (typeof chart === 'object' && chart !== null && chart.type === 'file' && chart.path) {
                      try {
                        const fileUrl = `${API_ENDPOINTS.taurus}/api/files/view?path=${encodeURIComponent(chart.path)}`;
                        const response = await fetch(fileUrl);
                        if (response.ok) {
                          const blob = await response.blob();
                          const base64Data = await new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              const base64 = reader.result.split(',')[1];
                              resolve(base64);
                            };
                            reader.onerror = reject;
                            reader.readAsDataURL(blob);
                          });
                          return {
                            type: 'matplotlib',
                            data: base64Data
                          };
                        } else {
                          console.error(`Failed to fetch Taurus chart: ${fileUrl}`, response.status);
                          return null;
                        }
                      } catch (err) {
                        console.error('Failed to convert Taurus file chart to base64:', err);
                        return null;
                      }
                    }
                    // If chart is already an object with base64 data (Virgo), keep it
                    else if (typeof chart === 'object' && chart !== null) {
                      return chart;
                    }
                    return null;
                  })
                );

                // Filter out any failed conversions
                const validCharts = processedCharts.filter(c => c !== null);
                if (validCharts.length > 0) {
                  transformed.charts = validCharts;
                }
              }
            }

            if (msg.tables) {
              transformed.tables = msg.tables;
            }
            if (msg.codeSnippets) {
              transformed.codeSnippets = msg.codeSnippets;
            }

            return transformed;
          })
      );

      // Validate that we have messages to share
      if (transformedMessages.length === 0) {
        setError('No messages to share. Please have a conversation first.');
        setCreating(false);
        return;
      }

      const finalDatasource = hideDatasource ? 'Database' : datasource;

      const requestBody = {
        messages: transformedMessages,
        datasource: finalDatasource,
        mode,
        visibility: userHasOrg ? visibility : 'public',
        show_attribution: showAttribution,
        note: note.trim() || undefined // Only send if not empty
      };

      const response = await axios.post(
        `${API_ENDPOINTS.main}/chats/share`,
        requestBody,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Save preferences for next time (but NOT visibility - user must choose each time)
      const prefs = {
        showAttribution,
        hideDatasource,
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem(SHARE_PREFS_KEY, JSON.stringify(prefs));

      // Build full URL
      const fullUrl = `${window.location.origin}${response.data.share_url}`;
      setShareUrl(fullUrl);
      setCreated(true);

      // Copy to clipboard
      try {
        await navigator.clipboard.writeText(fullUrl);
      } catch (clipboardErr) {
        console.error('Failed to copy to clipboard:', clipboardErr);
      }

    } catch (err) {
      console.error('Error creating share:', err);
      console.error('Error response data:', err.response?.data);
      console.error('Error response status:', err.response?.status);

      if (err.response?.status === 429) {
        setError('Daily share limit reached. Try again tomorrow or upgrade your plan.');
      } else {
        // Handle both string and object error responses
        let errorMessage = 'Failed to create share. Please try again.';
        if (err.response?.data?.detail) {
          if (typeof err.response.data.detail === 'string') {
            errorMessage = err.response.data.detail;
          } else if (Array.isArray(err.response.data.detail)) {
            // Pydantic validation errors (array of objects)
            const detailedErrors = err.response.data.detail.map(e => {
              const loc = e.loc ? e.loc.join(' -> ') : 'unknown';
              return `${loc}: ${e.msg}`;
            }).join(' | ');
            errorMessage = `Validation error: ${detailedErrors}`;
            console.error('Pydantic validation errors:', err.response.data.detail);
          } else {
            errorMessage = JSON.stringify(err.response.data.detail);
          }
        }
        setError(errorMessage);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleClose = () => {
    setCreated(false);
    setShareUrl('');
    setError(null);
    setCopied(false);
    setNote('');
    onClose();
  };

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <div className="flex items-center gap-2">
            <Link className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {created ? 'Share Created!' : 'Share This Chat'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {created ? (
            /* Success View */
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-4">
                <Check className="h-6 w-6" />
                <span className="font-semibold">Link created and copied to clipboard!</span>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Share URL:</span>
                  <button
                    onClick={handleCopy}
                    className={`ml-auto p-1.5 rounded transition-all ${
                      copied
                        ? 'bg-green-100 dark:bg-green-900/30'
                        : 'hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                    title={copied ? 'Copied!' : 'Copy to clipboard'}
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                    ) : (
                      <Copy className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    )}
                  </button>
                </div>
                <div className="text-sm text-gray-900 dark:text-gray-100 break-all font-mono bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
                  {shareUrl}
                </div>
              </div>

              <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <p>• Expires in 7 days</p>
                <p>• {visibility === 'organization' ? 'Only members of your organization can view' : 'Anyone with the link can view'}</p>
                <p>• Charts and tables included</p>
              </div>

              <button
                onClick={handleClose}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            /* Creation View */
            <div className="space-y-3">
              {/* Datasource Info - Compact */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Database className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs text-blue-800 dark:text-blue-200">
                      Datasource: <span className="font-semibold">{hideDatasource ? 'Database (hidden)' : datasource}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Note Input - Optional message for viewers */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Add a note for viewers (optional)
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="E.g., 'This analysis shows Q4 sales trends' or 'Please review the conclusions section'"
                  maxLength={500}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                  rows={3}
                />
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                  {note.length}/500
                </div>
              </div>

              {/* Visibility Options - Always show, disable org if no membership */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Who can view?
                </label>

                <div className="space-y-2">
                  {/* Organization Option - Show always, disable if no org */}
                  <label
                    className={`flex items-center gap-2 p-2 border rounded-lg transition-colors ${
                      !userHasOrg
                        ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                        : visibility === 'organization'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 cursor-pointer'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer'
                    }`}
                    title={!userHasOrg ? 'Requires organization membership' : ''}
                  >
                    <input
                      type="radio"
                      value="organization"
                      checked={visibility === 'organization'}
                      onChange={(e) => setVisibility(e.target.value)}
                      disabled={!userHasOrg}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                    />
                    <Users className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {user?.org_name || 'My Organization'}
                      </span>
                      {!userHasOrg && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 block">
                          Requires organization membership
                        </span>
                      )}
                    </div>
                  </label>

                  {/* Public Option - Always available */}
                  <label
                    className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer transition-colors ${
                      visibility === 'public'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <input
                      type="radio"
                      value="public"
                      checked={visibility === 'public'}
                      onChange={(e) => setVisibility(e.target.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <Globe className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      Anyone with the link
                    </span>
                  </label>
                </div>
              </div>

              {/* Options - Compact checkboxes */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hideDatasource}
                    onChange={(e) => setHideDatasource(e.target.checked)}
                    className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Hide datasource name (show as "Database")
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showAttribution}
                    onChange={(e) => setShowAttribution(e.target.checked)}
                    className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Show "Shared by {user?.email}"
                  </span>
                </label>
              </div>

              {/* Summary Box - Compact */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2 border border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-900 dark:text-gray-100">
                  <span className="font-semibold">Summary:</span> This will create a link that expires in 7 days.{' '}
                  {visibility === 'organization'
                    ? 'Only members of your organization can view it.'
                    : 'Anyone with the link can view it.'}
                </p>
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Link className="h-4 w-4" />
                      Create Share Link
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ShareChatModal;
